import React, { useState, useEffect, useRef } from 'react';
import { JBAlert, JBButton, JBTypography, JBCodeInput } from '@jbhi-fi/lanyard-ui';
import ReceiptLayout from '../receipt.layout';
import ReceiptHelper from '../helpers/receipt.helper';
import { useReceiptContext } from '../context/receipt.context';
import apiPDFDownload from '../../../../utils/api-pdf-download';
import { getHashedCustomerEmail } from '../../../../utils/event-tracking/event-data';
import { useTrackingContext, formAction, trackingContext } from '@jbhi-fi/components/src/Tracking';

const receiptHelper = new ReceiptHelper();

const validationMessages = {
  required: 'That code is invalid. All 4 digits are required.',
  invalid: "That code is invalid. Give it another go or tap 'Resend'.",
};

const VERIFY_ERROR_HEADING = "Can't verify receipt";
const VERIFY_ERROR_CONTENT = 'We were unable to verify your receipt, please contact customer service.';

const VerifyView = ({ receiptId, history }) => {
  const { data, isLoading, setReceiptData, isLinked } = useReceiptContext();
  const { tracker } = useTrackingContext();
  const inputBlock = useRef(null);
  const [verifyError, setVerifyError] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [code, setCode] = useState(null);
  const [validationError, setValidationError] = useState(validationMessages.required);

  // Used with key on JBCodeInput as a hack to force remount (reset) of component on change
  const [codeInputKey, setCodeInputKey] = useState(1);

  const isDownload = window.location.href.includes('download=true');

  useEffect(() => {
    if (receiptId && receiptId === data?.receipt?.ReceiptData?.Code) {
      if (!data.verify && !data.receipt.MessageReference) {
        history.push(`/${receiptId}?autoDownload=false`);
      } else if ((isLinked || data.verify?.raw?.data?.phone) && !isDownload) {
        history.push(`/${receiptId}/already-claimed`);
      } else if (
        data.receipt.ErrorCode &&
        (data.receipt.ErrorCode === 'InvalidReceiptCode' || data.receipt.ErrorCode === 'InvalidData')
      ) {
        history.push(`/?autoDownload=false`);
      }
    }
  }, [data]);

  useEffect(() => {
    tracker.group = 'enter otp';
    tracker.extraData = { ...tracker.extraData, hashedEmail: getHashedCustomerEmail(), data: { value: receiptId } };
    tracker.trackFormEvent({ action: formAction.stepView });
  }, []);

  useEffect(() => {
    if (verifyError) {
      tracker.trackError({
        errorMsg: `${VERIFY_ERROR_HEADING}. ${VERIFY_ERROR_CONTENT}`,
        contextOverride: trackingContext.form,
      });
    }
  }, [verifyError]);

  const formatMobileNumber = number => `${number.substr(0, 4)} ${number.substr(4, 3)} ${number.substr(7)}`;

  const getMobileNumber = () => {
    if (data.receipt.MobileNumber) {
      return formatMobileNumber(data.receipt.MobileNumber.replace(' ', ''));
    } else if (data?.receipt?.ReceiptData?.MobileNumber) {
      return formatMobileNumber(data.receipt.ReceiptData.MobileNumber.replace(' ', ''));
    }
    return '';
  };

  const verifyCode = async event => {
    event.preventDefault();
    setResendSuccess(false);
    setVerifyError(null);
    tracker.trackClick({ label: 'Verify' });

    if (code?.length === 4) {
      setValidationError(null);
      setVerifying(true);

      tracker.trackFormEvent({ label: 'Verify', action: formAction.submit });

      try {
        const result = await receiptHelper.verifyReceipt(receiptId, code, data?.receipt.MessageReference);

        if (!result.ErrorCode) {
          setReceiptData(result);
        }

        try {
          const downloadResponse = await receiptHelper.downloadReceipt(receiptId, result.ReceiptToken);

          if (downloadResponse.ErrorCode) {
            setValidationError(validationMessages.invalid);
            setVerifying(false);
          } else {
            tracker.trackFormEvent({ action: formAction.complete });

            apiPDFDownload(downloadResponse, `JBHiFi-Receipt-${receiptId}`);
            setTimeout(() => {
              history.push(`/${receiptId}?autoDownload=false`);
            }, 1000);
          }
        } catch (error) {
          setValidationError(validationMessages.invalid);
          setVerifying(false);
        }
      } catch (error) {
        setVerifying(false);
        setVerifyError(false);
      }
    } else {
      const codeInput = document.querySelector('input[name="code_input"]');
      codeInput.focus();
      codeInput.blur();
      tracker.trackError({ errorMsg: validationMessages.required, contextOverride: trackingContext.form });
      setValidationError(validationMessages.required);
    }
  };

  const resendCode = () => {
    setResendSuccess(false);
    setResending(true);
    setCode(null);
    setVerifyError(null);
    setValidationError(validationMessages.required);
    setCodeInputKey(codeInputKey + 1);

    tracker.trackClick({ label: 'resend' });

    receiptHelper
      .sendReceiptVerification(receiptId)
      .then(result => {
        if (!result.ErrorCode) {
          setReceiptData({ ...data.receipt, MessageReference: result.MessageReference });
          setResendSuccess(true);
        } else if (result.ErrorCode === 'ForbiddenReceiptCode') {
          setReceiptData(result);
          history.push(`/lockedout`);
        }
        setResending(false);
        return result;
      })
      .catch(error => console.error(error, 'something went wrong with resending code'));
  };

  const handleChange = event => {
    setCode(event.target.value);
    if (event.target.value.length < 4) {
      setValidationError(validationMessages.required);
    } else {
      setValidationError(null);
    }
  };

  return (
    <ReceiptLayout ripped isLoading={isLoading}>
      {resendSuccess ? <JBAlert showIcon type="success" heading="Verification code sent" /> : null}
      {verifyError ? (
        <JBAlert showIcon type="error" heading={VERIFY_ERROR_HEADING} content={VERIFY_ERROR_CONTENT} />
      ) : null}
      <div className="primary-block">
        <JBTypography align="center" type="h5" margin="0 0 25px">
          Download your receipt by
          <br />
          entering the the verification
          <br />
          code we sent to
          <br />
          {getMobileNumber()}
        </JBTypography>

        <form onSubmit={verifying ? event => event.preventDefault() : verifyCode}>
          <div ref={inputBlock} className="code-inputs">
            <JBCodeInput
              key={codeInputKey}
              name="code_input"
              autoFocus
              label="Enter 4-digit verification code"
              isRequired
              length={4}
              errorShow={validationError === validationMessages.invalid ? true : undefined}
              onChange={handleChange}
              errorMessage={validationError}
              inputMode="numeric"
              errorShowDelay={150}
            />
          </div>
          <JBTypography align="center" type="p" margin="10px 0 25px">
            <button type="button" className="link" onClick={resending ? null : resendCode}>
              {resending ? 'Resending' : 'Resend'}
            </button>
          </JBTypography>
          <JBButton
            type="primary"
            fullWidth={true}
            label={verifying ? 'Verifying...' : 'Verify'}
            onClick={verifying ? null : verifyCode}
            submit
          />
        </form>
      </div>
    </ReceiptLayout>
  );
};

export default VerifyView;
